import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  ArrowLeft, RefreshCcw, Mail, Search, AlertTriangle, CheckCircle2, Pause, Ban, Inbox, ShieldX,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_META = {
  sent:     { label: 'Sent',     klass: 'bg-emerald-100 text-emerald-800 border-emerald-300', Icon: CheckCircle2 },
  paused:   { label: 'Paused',   klass: 'bg-amber-100   text-amber-900   border-amber-300',   Icon: Pause },
  disabled: { label: 'Disabled', klass: 'bg-slate-100   text-slate-700   border-slate-300',   Icon: Ban },
  error:    { label: 'Error',    klass: 'bg-rose-100    text-rose-800    border-rose-300',    Icon: ShieldX },
};

const SG_STATUS_META = {
  delivered: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  processed: 'bg-sky-100     text-sky-800     border-sky-300',
  bounce:    'bg-rose-100    text-rose-800    border-rose-300',
  blocked:   'bg-rose-100    text-rose-800    border-rose-300',
  spam:      'bg-rose-100    text-rose-800    border-rose-300',
  deferred:  'bg-amber-100   text-amber-900   border-amber-300',
  dropped:   'bg-slate-200   text-slate-800   border-slate-400',
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || { label: status, klass: 'bg-gray-100 text-gray-800', Icon: Mail };
  const { Icon } = meta;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold uppercase ${meta.klass}`}
      data-testid={`status-badge-${status}`}
    >
      <Icon className="w-3 h-3" /> {meta.label}
    </span>
  );
};

const relTime = (iso) => {
  if (!iso) return '—';
  const then = new Date(iso);
  const diff = (Date.now() - then.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return then.toLocaleString();
};

const EmailLog = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '200', include_sendgrid: 'true' });
      if (statusFilter) params.set('status', statusFilter);
      if (q.trim()) params.set('q', q.trim());
      const res = await api.get(`/admin/system/email-log?${params.toString()}`);
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load email log');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, q]);

  useEffect(() => { load(); }, [load]);

  const sg = data?.sendgrid || { events: [], error: null };
  const summary = data?.summary_24h || {};

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="admin-email-log">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/system/email-health')} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Email Health
        </Button>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Inbox className="w-6 h-6 text-indigo-600" /> Email Log
        </h1>
        <Button variant="outline" size="sm" className="ml-auto" onClick={load} data-testid="refresh-btn">
          <RefreshCcw className="w-3.5 h-3.5 mr-1" /> Refresh
        </Button>
      </div>

      {/* 24h summary stripe */}
      <Card className="mb-5">
        <CardContent className="p-4 flex flex-wrap gap-3 items-center text-sm">
          <span className="font-semibold text-gray-700">Last 24h:</span>
          <Badge className="bg-slate-100 text-slate-800 border-slate-300" data-testid="summary-total">
            {summary.total || 0} total
          </Badge>
          {['sent', 'paused', 'disabled', 'error'].map((s) => (
            summary[s] ? (
              <span key={s} data-testid={`summary-${s}`}>
                <StatusBadge status={s} />
                <span className="ml-1 text-xs text-gray-600">×{summary[s]}</span>
              </span>
            ) : null
          ))}
        </CardContent>
      </Card>

      {/* Filters */}
      <Card className="mb-5">
        <CardContent className="p-4 flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-2 flex-1 min-w-[220px]">
            <Search className="w-4 h-4 text-gray-500" />
            <Input
              placeholder="Search recipient, subject, or category…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && load()}
              className="flex-1"
              data-testid="search-input"
            />
          </div>
          <div className="flex items-center gap-1" data-testid="status-filter-group">
            {['', 'sent', 'paused', 'disabled', 'error'].map((s) => (
              <Button
                key={s || 'all'}
                size="sm"
                variant={statusFilter === s ? 'default' : 'outline'}
                onClick={() => setStatusFilter(s)}
                className="text-xs capitalize"
                data-testid={`filter-${s || 'all'}`}
              >
                {s || 'All'}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Local events */}
      <Card className="mb-6">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            Local send attempts
            <span className="text-xs text-gray-500 font-normal">
              (authoritative — every call to email service logs one row)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading && !data ? (
            <div className="p-6 text-sm text-gray-500">Loading…</div>
          ) : (data?.events || []).length === 0 ? (
            <div className="p-6 text-sm text-gray-500" data-testid="empty-state">
              No email events match the current filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="email-log-table">
                <thead className="bg-gray-50 text-xs uppercase text-gray-600">
                  <tr>
                    <th className="text-left px-4 py-2 font-semibold">Status</th>
                    <th className="text-left px-4 py-2 font-semibold">Recipient</th>
                    <th className="text-left px-4 py-2 font-semibold">Subject</th>
                    <th className="text-left px-4 py-2 font-semibold">Category</th>
                    <th className="text-left px-4 py-2 font-semibold">When</th>
                    <th className="text-left px-4 py-2 font-semibold">Detail</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.events.map((e) => (
                    <tr key={e.id} data-testid={`email-row-${e.id}`}>
                      <td className="px-4 py-2"><StatusBadge status={e.status} /></td>
                      <td className="px-4 py-2 font-mono text-xs">{e.to_email}</td>
                      <td className="px-4 py-2 max-w-[280px] truncate" title={e.subject || ''}>
                        {e.subject || <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-2">
                        {e.category ? (
                          <span className="inline-block bg-indigo-50 text-indigo-700 border border-indigo-200 rounded px-2 py-0.5 text-[11px]">
                            {e.category}
                          </span>
                        ) : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-600" title={e.created_at || ''}>
                        {relTime(e.created_at)}
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-600">
                        {e.status === 'error' && e.error_message && (
                          <span className="text-rose-700 inline-flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            <span className="truncate max-w-[220px]" title={e.error_message}>{e.error_message}</span>
                          </span>
                        )}
                        {e.status === 'paused' && e.pause_source && (
                          <span>source: <span className="font-mono">{e.pause_source}</span></span>
                        )}
                        {e.status === 'sent' && e.status_code && (
                          <span className="font-mono text-emerald-700">HTTP {e.status_code}</span>
                        )}
                        {e.message_id && (
                          <span className="ml-2 text-gray-400 font-mono text-[10px]" title={`SendGrid X-Message-Id: ${e.message_id}`}>
                            #{e.message_id.slice(0, 8)}…
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* SendGrid enrichment */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            SendGrid Activity
            <span className="text-xs text-gray-500 font-normal">
              (post-send state: delivered / bounced / spam)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {sg.error ? (
            <div className="text-xs text-gray-500" data-testid="sendgrid-error">
              {sg.error === 'sendgrid_403' ? (
                <>
                  <AlertTriangle className="w-3 h-3 inline mr-1 text-amber-500" />
                  SendGrid API key lacks <code>email_activity.read</code> permission.
                  Add the scope in SendGrid → Settings → API Keys to unlock this panel.
                </>
              ) : sg.error === 'no_api_key' ? (
                <>No <code>SENDGRID_API_KEY</code> configured.</>
              ) : sg.error === 'skipped' ? (
                <>SendGrid fetch was skipped for this request.</>
              ) : (
                <>Fallback unavailable: {sg.error}</>
              )}
            </div>
          ) : (sg.events || []).length === 0 ? (
            <div className="text-xs text-gray-500">No Activity events returned.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="sendgrid-activity-table">
                <thead className="bg-gray-50 text-xs uppercase text-gray-600">
                  <tr>
                    <th className="text-left px-4 py-2 font-semibold">SG Status</th>
                    <th className="text-left px-4 py-2 font-semibold">Recipient</th>
                    <th className="text-left px-4 py-2 font-semibold">Subject</th>
                    <th className="text-left px-4 py-2 font-semibold">Opens</th>
                    <th className="text-left px-4 py-2 font-semibold">Clicks</th>
                    <th className="text-left px-4 py-2 font-semibold">Last Event</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sg.events.map((e, i) => (
                    <tr key={`${e.message_id || i}`} data-testid={`sendgrid-row-${i}`}>
                      <td className="px-4 py-2">
                        <span
                          className={`inline-block rounded-full border px-2 py-0.5 text-[11px] font-bold uppercase ${SG_STATUS_META[e.status] || 'bg-gray-100 text-gray-800 border-gray-300'}`}
                        >
                          {e.status}
                        </span>
                      </td>
                      <td className="px-4 py-2 font-mono text-xs">{e.to_email}</td>
                      <td className="px-4 py-2 max-w-[280px] truncate" title={e.subject || ''}>
                        {e.subject || '—'}
                      </td>
                      <td className="px-4 py-2 text-xs">{e.opens}</td>
                      <td className="px-4 py-2 text-xs">{e.clicks}</td>
                      <td className="px-4 py-2 text-xs text-gray-600">{relTime(e.last_event_time)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailLog;
